/**
 * Report a section the first time half of it is on screen, once per page view.
 *
 * Exists so a hypothesis about a section can be measured: a click event says what
 * someone did, this says what they saw before doing it (#1162). Without
 * IntersectionObserver (prerender, very old browsers) it does nothing.
 */

import { useEffect, useRef, type RefObject } from 'react'

import { capture } from '@/shared/lib/analytics'

export function useSeenOnce<T extends Element>(
  event: string,
  properties?: Record<string, string | number | boolean>,
): RefObject<T> {
  const ref = useRef<T>(null)
  // Read once: the event describes the first sighting, not later state.
  const props = useRef(properties)

  useEffect(() => {
    const node = ref.current
    if (!node || typeof IntersectionObserver === 'undefined') return undefined
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          capture(event, props.current)
          observer.disconnect()
        }
      },
      { threshold: 0.5 },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [event])

  return ref
}
